import { REQUEST_TIMEOUT_MS } from '@ctcells/core'

import { orchestrator } from '../clients.ts'
import { createCommand, newCommandId } from '../command.ts'
import { keys } from '../keys.ts'

/**
 * 모듈 **초기화 중계**(`IFC-RO-44`) — `POST /modules/{id}/control/initialize`.
 *
 * 🔴 **왜 필요한가**(eric 2026-09-28 「모듈 초기화 버튼 만들어줘」). 작업 「중지」 뒤 실행 취소가
 *    모듈에 닿아도 module-core 가 취소 뒤 상태를 정리하지 않아 모듈이 `PIPETTING` · `SHAKING` 같은
 *    작업 상태에 남는다(`docs/backend-requests.csv` BR-97). 그때 `allowed_commands` 도 비어 화면에서 풀 길이 없었다.
 *
 * 실물(`openapi.json` · `ctcells-core/app/views/module_control.py` `_plain(mc.INITIALIZE)` · 2026-09-28)
 *
 * | 칸 | 값 |
 * | --- | --- |
 * | 본문 | `CommandEnvelope{command_id(필수)}` · **본문 자체는 선택**(`Body | None`) |
 * | 헤더 | `X-Control-Token` — `require_control_lock` 의존(`controlToken.ts` 표에 등재) |
 * | 역할 | `mc.WRITE_ROLES` = operator · admin (`/auth/me` operationId `initializeModuleControl`) |
 * | 성공 | **202** `{module_id, command_id}` (openapi 는 200 · 빈 스키마 — 실물이 202 다) |
 * | 거절 | 409 `MODULE_LOCAL_MODE` · 409 `MODULE_BUSY`(`reason=robot_holding_tray` — 로봇이 파지 중(쥠·모름) · v0.9.1) · 409 `E_STOP`(시스템 안전 상태가 막을 때 — 초기화는 «움직이는 쪽»이다) · 409 `MODULE_IN_ERROR`(모듈이 `ERROR` — 출구는 `/recovery/start` · 모듈 거절) · 503(모듈에 못 닿음) · 모듈 거절은 그 코드 그대로 |
 * | 걷힌 거절 | `MODULE_BUSY(batch_running)` — v0.9.1(core `75fca33`)부터 배치 중에도 받는다 |
 *
 * 🔴 **`/modules/prepare`(배치 준비 강제 정리)와 다르다.** 그쪽은 멈춘 작업까지 폐기한다 — 이것은
 *    모듈 한 대에 `initialize` 하나를 중계할 뿐이다.
 * 🔴 **`command_id` 를 늘 싣는다.** 재시도에 같은 값을 넘기면 서버가 첫 결과를 돌려준다(멱등).
 */

/** 중계 응답(202). 결과는 모듈 상태 구독으로 온다 — 낙관적으로 쓸 값이 없다 */
export interface ModuleInitializeAccepted {
  module_id?: string
  command_id?: string
}

export interface InitializeModuleVars {
  moduleId: string
  /**
   * 원심 로터 정지를 사람이 눈으로 확인했다(계약 `rotor_stop_confirmed` · 06 `SM-MOD-23` ⓐ) — 원심이 `ABORTED` · `INTERRUPTED` 에서
   * 원점 복귀하려면 참이어야 한다(없으면 모듈 409 · `ModuleSnapshot.exit_blockers` 의 `rotor_stop_unconfirmed`).
   * 🔴 참일 때만 싣는다 — 서버가 «모르면 칸째 없음»(`ABSENT_WHEN_UNKNOWN`)으로 받는다. 화면이 값을 지어내지 않는다(사람이 확인한 때만)
   */
  rotorStopConfirmed?: boolean | undefined
  /** 재시도 시 **같은 값을 다시 넘긴다** */
  commandId?: string
}

/** 요청 한 건 — 본문 모양의 정의처는 여기 하나다 */
export function initializeModule({
  moduleId,
  rotorStopConfirmed,
  commandId,
}: InitializeModuleVars): Promise<ModuleInitializeAccepted> {
  return orchestrator.request<ModuleInitializeAccepted>({
    method: 'POST',
    path: `/modules/${encodeURIComponent(moduleId)}/control/initialize`,
    body: {
      command_id: commandId ?? newCommandId(),
      ...(rotorStopConfirmed === true ? { rotor_stop_confirmed: true } : {}),
    },
    timeoutMs: REQUEST_TIMEOUT_MS.command,
    retryOn401: true,
  })
}

export const useInitializeModule = createCommand<InitializeModuleVars, ModuleInitializeAccepted>({
  send: initializeModule,
  // 🔴 상태는 구독이 바꾼다. 무효화는 스냅샷을 한 번 다시 받게 할 뿐이다(DP-04)
  invalidates: () => [keys.modules.all()],
})

/**
 * 모듈 **시퀀스 중계** — `POST /modules/{id}/control/sequences`.
 *
 * 실물(`openapi.json` `ModuleSequenceRunRequest` · `ctcells-core/app/views/module_control.py` `run_module_sequence_control` · 2026-09-28)
 *
 * | 칸 | 값 |
 * | --- | --- |
 * | 본문 | `{command_id(필수), sequence(필수), params?}` — `params` 는 `sequence_schemas[sequence]` 로 L2 가 검증, 없으면 빈 객체 |
 * | 헤더 | `X-Control-Token` — `require_control_lock`(`controlToken.ts` 표에 등재) |
 * | 성공 | 202 `{module_id, command_id, execution_id}` — `execution_id` 는 서버가 모듈 응답에서 옮겨 싣는다(없으면 502) · 결과는 구독으로 온다 |
 * | 거절 | `MODULE_LOCAL_MODE` · 모듈 거절은 그 코드 그대로(`ERROR` 면 `MODULE_IN_ERROR` — 정지 단건만 예외) · `MODULE_BUSY(batch_running)` 은 v0.9.1 에서 걷혔다 |
 * | 상태 | 🔴 v0.9.1 부터 **단건은 모듈 `state` 를 바꾸지 않는다**(06 `SM-MOD-21`) — 도는 동안은 `current_execution` 이 드러낸다 |
 *
 * 🔴 **지금 부르는 곳은 개발용 자동 준비 하나다**(`devQuickReset.ts` — 팔 복귀 `move_to_module` · 도어 `close_door`).
 *    ⚠ HANDOFF 6축에 단건 `release` 를 보내던 자리는 걷었다 — v0.9.1 단건은 `state` 를 안 바꿔 로봇이 HANDOFF 에 남는다
 *    (2026-09-29 실측). 쥔 트레이는 수동 이송 `place_held`(`commands/manualTransfer.ts`)로 놓는다.
 *    ⚠ 이 창구는 `block` 을 받지 않는다(`ModuleSequenceRunRequest` = `{command_id, sequence, params}`) — 모듈 로컬의 블록 꼴
 *    `release` 는 `HANDOFF → MOVING` 을 열지만 L2 로는 보낼 길이 없다(2026-09-29 팀장 · 쵸단 대조 · `docs/backend-requests.csv` BR-100).
 * ⚠ `params` 는 늘 싣는다(빈 객체) — 서버 기본값과 같고, 키를 빼는 갈래를 두지 않는다.
 */
/** 시퀀스 중계 응답(202) — `ctcells-core` `_relay` 의 `mc.SEQUENCES` 갈래 */
export interface ModuleSequenceAccepted extends ModuleInitializeAccepted {
  execution_id?: string
}

export interface RunModuleSequenceVars {
  moduleId: string
  sequence: string
  params?: Record<string, unknown>
  /** 재시도 시 **같은 값을 다시 넘긴다** */
  commandId?: string
}

export function runModuleSequence({
  moduleId,
  sequence,
  params,
  commandId,
}: RunModuleSequenceVars): Promise<ModuleSequenceAccepted> {
  return orchestrator.request<ModuleSequenceAccepted>({
    method: 'POST',
    path: `/modules/${encodeURIComponent(moduleId)}/control/sequences`,
    body: { command_id: commandId ?? newCommandId(), sequence, params: params ?? {} },
    timeoutMs: REQUEST_TIMEOUT_MS.command,
    retryOn401: true,
  })
}

export const useRunModuleSequence = createCommand<RunModuleSequenceVars, ModuleSequenceAccepted>({
  send: runModuleSequence,
  invalidates: () => [keys.modules.all()],
})

/**
 * 모듈 **진단 중계**(`IFC-RO-48`) — `POST /modules/{id}/control/diagnostics`.
 *
 * 실물(`openapi.json` · `ctcells-core/app/views/module_control.py` `_plain(mc.DIAGNOSTICS)` · 2026-10-22 로컬 실측)
 *
 * | 칸 | 값 |
 * | --- | --- |
 * | 본문 | `CommandEnvelope{command_id}` · 본문 자체는 선택 |
 * | 헤더 | `X-Control-Token` — `require_control_lock`(`controlToken.ts` 표에 등재 · 없으면 409 `CONTROL_LOCK_HELD` `no_token_header` 실측) |
 * | 역할 | `mc.WRITE_ROLES` = operator · admin(`/auth/me` operationId `runModuleDiagnosticsControl`) |
 * | 성공 | 200 — **모듈 응답 그대로**(`module_service` `DiagnosticsReport{passed, checks[{check_id, result, context}]}`) |
 *
 * 실측 원문(assembler-1 · sim): `{"passed":true,"checks":[{"check_id":"safety.estop_circuit","result":"pass",…},
 * {"check_id":"safety.interlock","result":"pass",…}]}`.
 * 🔴 `check_id` 어휘는 정적 enum 이 없다(모듈이 런타임 선언 · `DEC-12`) — 화면은 원값을 보인다.
 * 🔴 `result: unknown` 을 `pass` 로 접지 않는다(계약 원문).
 */
export interface ModuleDiagnosticsCheck {
  check_id: string
  /** `pass` · `fail` · `skipped` · `unknown` — 모르는 값은 원값 그대로 보인다 */
  result: string
  context?: Record<string, unknown>
}

export interface ModuleDiagnosticsReport {
  passed?: boolean
  checks?: ModuleDiagnosticsCheck[]
}

export interface RunModuleDiagnosticsVars {
  moduleId: string
  /** 재시도 시 **같은 값을 다시 넘긴다** */
  commandId?: string
}

export function runModuleDiagnostics({
  moduleId,
  commandId,
}: RunModuleDiagnosticsVars): Promise<ModuleDiagnosticsReport> {
  return orchestrator.request<ModuleDiagnosticsReport>({
    method: 'POST',
    path: `/modules/${encodeURIComponent(moduleId)}/control/diagnostics`,
    body: { command_id: commandId ?? newCommandId() },
    timeoutMs: REQUEST_TIMEOUT_MS.command,
    retryOn401: true,
  })
}

/** 🔴 `X-Control-Token` 대상. 진단은 모듈 상태를 바꾸지 않는다 — 결과는 응답 본문이 전부다(무효화 없음) */
export const useRunModuleDiagnostics = createCommand<
  RunModuleDiagnosticsVars,
  ModuleDiagnosticsReport
>({
  send: runModuleDiagnostics,
  invalidates: () => [],
})
